/**
 * Stripe Payment Links per product line and plan.
 * Shared links are preferred; currency-specific vars are only fallbacks.
 * Set REACT_APP_* in .env — see docs/STRIPE_PRICING_ENV.md
 */

const LEGACY_FALLBACKS = {
  monthly:
    process.env.REACT_APP_STRIPE_URL ||
    'https://buy.stripe.com/5kQcN54cK8JbeaK2KvaR206',
  '3months':
    process.env.REACT_APP_STRIPE_URL_3MONTHS ||
    'https://buy.stripe.com/5kQ7sL9x45wZ9Uu84PaR20o',
  '11months':
    process.env.REACT_APP_STRIPE_URL_11MONTHS ||
    process.env.REACT_APP_STRIPE_URL_12MONTHS ||
    'https://buy.stripe.com/cNi4gz24C0cF8QqacXaR20p',
};

export const DISPLAY_CURRENCY_RATES = {
  EUR: { symbol: '€', rate: 1, name: 'Euro' },
  XOF: { symbol: 'CFA', rate: 655.957, name: 'Franc CFA' },
  USD: { symbol: '$', rate: 1.08, name: 'US Dollar' },
  CAD: { symbol: 'CA$', rate: 1.47, name: 'Canadian Dollar' },
};

export const DISPLAY_CURRENCY_CODES = Object.keys(DISPLAY_CURRENCY_RATES);
export const UNIFIED_DISPLAY_CURRENCIES = ['EUR', 'USD', 'XOF', 'CAD'];

/** Aligned with marketing `USD_BILLING_COUNTRIES` — graduate USD-canonical regions. */
export const GRADUATE_USD_BASE_COUNTRY_CODES = new Set([
  'US',
  'GB',
  'CA',
  'NG',
  'GH',
  'KE',
  'ZA',
  'UG',
  'TZ',
  'RW',
  'ZM',
  'ZW',
  'MW',
  'BW',
  'NA',
  'SL',
  'LR',
  'GM',
  'SS',
  'SD',
  'ET',
  'ER',
  'SO',
  'DJ',
  'SC',
  'MU',
  'BI',
  'LS',
  'SZ',
  'IE',
  'AU',
  'NZ',
  'JM',
  'TT',
  'BS',
  'BB',
  'BZ',
]);

export const EU_EEA_UK_CH_COUNTRY_CODES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'NO',
  'LI',
  'GB',
  'CH',
]);

export const CFA_ZONE_COUNTRY_CODES = new Set([
  'BJ',
  'BF',
  'CI',
  'GW',
  'ML',
  'NE',
  'SN',
  'TG',
  'CM',
  'CF',
  'TD',
  'CG',
  'GA',
  'GQ',
  'ST',
]);

/** @deprecated Prefer {@link GRADUATE_USD_BASE_COUNTRY_CODES} — kept for call sites using the old name. */
export const ANGLOPHONE_COUNTRY_CODES = GRADUATE_USD_BASE_COUNTRY_CODES;

const FAMILIES_FIXED_XOF = {
  monthly: 50000,
  '3months': 145000,
  '12months': 550000,
};

const STANDARD_LINK_CONFIG = {
  graduate_full: {
    monthly: {
      shared: 'REACT_APP_STRIPE_GRADUATE_FULL_MONTHLY',
      eur: 'REACT_APP_STRIPE_GRADUATE_FULL_MONTHLY_EUR',
      usd: 'REACT_APP_STRIPE_GRADUATE_FULL_MONTHLY_USD',
    },
    '3months': {
      shared: 'REACT_APP_STRIPE_GRADUATE_FULL_3MONTHS',
      eur: 'REACT_APP_STRIPE_GRADUATE_FULL_3MONTHS_EUR',
      usd: 'REACT_APP_STRIPE_GRADUATE_FULL_3MONTHS_USD',
    },
    '12months': {
      shared: 'REACT_APP_STRIPE_GRADUATE_FULL_12MONTHS',
      eur: 'REACT_APP_STRIPE_GRADUATE_FULL_12MONTHS_EUR',
      usd: 'REACT_APP_STRIPE_GRADUATE_FULL_12MONTHS_USD',
    },
  },
  graduate_ielts: {
    monthly: {
      shared: 'REACT_APP_STRIPE_GRADUATE_IELTS_MONTHLY',
      eur: 'REACT_APP_STRIPE_GRADUATE_IELTS_MONTHLY_EUR',
      usd: 'REACT_APP_STRIPE_GRADUATE_IELTS_MONTHLY_USD',
    },
    '3months': {
      shared: 'REACT_APP_STRIPE_GRADUATE_IELTS_3MONTHS',
      eur: 'REACT_APP_STRIPE_GRADUATE_IELTS_3MONTHS_EUR',
      usd: 'REACT_APP_STRIPE_GRADUATE_IELTS_3MONTHS_USD',
    },
    '12months': {
      shared: 'REACT_APP_STRIPE_GRADUATE_IELTS_12MONTHS',
      eur: 'REACT_APP_STRIPE_GRADUATE_IELTS_12MONTHS_EUR',
      usd: 'REACT_APP_STRIPE_GRADUATE_IELTS_12MONTHS_USD',
    },
  },
  /** Executive: single EUR-canonical Payment Link per plan (no separate EUR/USD env vars). */
  executive: {
    '3months': {
      shared: 'REACT_APP_STRIPE_EXECUTIVE_3MONTHS',
    },
    complete: {
      shared: 'REACT_APP_STRIPE_EXECUTIVE_COMPLETE',
    },
  },
};

const LEGACY_GRADUATE_LINK_CONFIG = {
  monthly: {
    shared: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_MONTHLY',
    eur: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_MONTHLY_EUR',
    usd: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_MONTHLY_USD',
  },
  '3months': {
    shared: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_3MONTHS',
    eur: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_3MONTHS_EUR',
    usd: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_3MONTHS_USD',
  },
  '11months': {
    shared: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_11MONTHS',
    eur: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_11MONTHS_EUR',
    usd: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_11MONTHS_USD',
    eurAlias: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_12MONTHS_EUR',
    usdAlias: 'REACT_APP_STRIPE_LEGACY_GRADUATE_FULL_12MONTHS_USD',
  },
};

const GRADUATE_FULL_EUR = {
  monthly: 80,
  '3months': 216,
  '12months': 800,
};

const GRADUATE_FULL_USD = {
  monthly: 80,
  '3months': 216,
  '12months': 800,
};

const GRADUATE_IELTS_EUR = {
  monthly: 50,
  '3months': 135,
  '12months': 500,
};

const GRADUATE_IELTS_USD = {
  monthly: 50,
  '3months': 135,
  '12months': 500,
};

const GRADUATE_FULL_LEGACY_EUR = {
  monthly: 70,
  '3months': 189,
  '11months': 700,
};

const GRADUATE_FULL_LEGACY_USD = {
  monthly: 70,
  '3months': 189,
  '11months': 700,
};

const AMOUNTS = {
  families: {
    monthly: { eur: 85, usd: Math.round(85 * 1.08) },
    '3months': { eur: 246, usd: Math.round(246 * 1.08) },
    '12months': { eur: 850, usd: Math.round(850 * 1.08) },
  },
  executive: {
    '3months': {
      eur: 300,
      usd: Math.round(300 * DISPLAY_CURRENCY_RATES.USD.rate),
    },
    complete: {
      eur: 1000,
      usd: Math.round(1000 * DISPLAY_CURRENCY_RATES.USD.rate),
    },
  },
};

function envValue(name) {
  if (!name) return null;
  const value = process.env[name];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function firstNonEmpty(...values) {
  return values.find(Boolean) || null;
}

export function normalizeBillingCurrency(value) {
  return String(value || '').toLowerCase() === 'usd' ? 'usd' : 'eur';
}

export function normalizeDisplayCurrency(value, fallback = 'EUR') {
  const upper = String(value || '').toUpperCase();
  return DISPLAY_CURRENCY_CODES.includes(upper) ? upper : fallback;
}

export function normalizeCountryCode(value) {
  const upper = String(value || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(upper) ? upper : null;
}

/**
 * Graduate EUR vs USD list-price anchor: EU/EEA/UK/CH + CFA first, then USD-base countries, else EUR.
 * @param {string|null|undefined} countryCode
 * @returns {'eur'|'usd'}
 */
export function graduateCanonicalBaseFromCountryCode(countryCode) {
  const n = normalizeCountryCode(countryCode);
  if (!n) return 'eur';
  if (EU_EEA_UK_CH_COUNTRY_CODES.has(n) || CFA_ZONE_COUNTRY_CODES.has(n)) return 'eur';
  if (GRADUATE_USD_BASE_COUNTRY_CODES.has(n)) return 'usd';
  return 'eur';
}

export function isAnglophoneCountryCode(countryCode) {
  const normalized = normalizeCountryCode(countryCode);
  return normalized ? GRADUATE_USD_BASE_COUNTRY_CODES.has(normalized) : false;
}

export function allowedDisplayCurrenciesForCountryCode(countryCode) {
  void countryCode;
  return UNIFIED_DISPLAY_CURRENCIES;
}

export function allowedDisplayCurrenciesForContext(countryCode, productLine) {
  void countryCode;
  void productLine;
  return UNIFIED_DISPLAY_CURRENCIES;
}

export function defaultDisplayCurrencyForCountryCode(countryCode) {
  void countryCode;
  return 'EUR';
}

export function defaultDisplayCurrencyForContext(countryCode, productLine) {
  void countryCode;
  void productLine;
  return 'EUR';
}

export function billingCurrencyHintForCountryCode(countryCode) {
  void countryCode;
  return 'eur';
}

function normalizePlanKey(planKey, pricingSegment) {
  if (
    pricingSegment === 'legacy_graduate' &&
    (planKey === '12months' || planKey === '11months')
  ) {
    return '11months';
  }
  if (planKey === '11months') return '12months';
  return planKey;
}

function resolveStandardLinkConfig(productLine, planKey) {
  return STANDARD_LINK_CONFIG[productLine]?.[planKey] || null;
}

function resolveLegacyLinkConfig(planKey) {
  return LEGACY_GRADUATE_LINK_CONFIG[planKey] || null;
}

function resolveDisplayAmount({ productLine, planKey, row, displayCurrency }) {
  if (displayCurrency === 'USD') return row.usd;
  if (displayCurrency === 'EUR') return row.eur;

  if (displayCurrency === 'XOF' && productLine === 'families') {
    return FAMILIES_FIXED_XOF[planKey] || Math.round(row.eur * 655.957);
  }

  return Math.round(row.eur * DISPLAY_CURRENCY_RATES[displayCurrency].rate);
}

/**
 * EUR/USD pair for graduate plans from regional canonical base.
 * @param {'graduate_full'|'graduate_ielts'} productLine
 * @param {string} planKey
 * @param {'standard'|'legacy_graduate'} pricingSegment
 * @param {'eur'|'usd'} pricingCanonicalBase
 * @returns {{ eur: number, usd: number }}
 */
export function graduateEurUsdRowForCanonicalBase({
  productLine,
  planKey,
  pricingSegment = 'standard',
  pricingCanonicalBase,
}) {
  const k = normalizePlanKey(planKey, pricingSegment);
  const isLegacy =
    productLine === 'graduate_full' && pricingSegment === 'legacy_graduate';
  const r = DISPLAY_CURRENCY_RATES.USD.rate;

  let eurAnchor;
  let usdAnchor;
  if (productLine === 'graduate_ielts') {
    eurAnchor = GRADUATE_IELTS_EUR[k];
    usdAnchor = GRADUATE_IELTS_USD[k];
  } else if (isLegacy) {
    eurAnchor = GRADUATE_FULL_LEGACY_EUR[k];
    usdAnchor = GRADUATE_FULL_LEGACY_USD[k];
  } else {
    eurAnchor = GRADUATE_FULL_EUR[k];
    usdAnchor = GRADUATE_FULL_USD[k];
  }

  if (eurAnchor == null || usdAnchor == null) {
    return { eur: 80, usd: Math.round(80 * r) };
  }

  if (pricingCanonicalBase === 'usd') {
    return {
      eur: Math.round(usdAnchor / r),
      usd: usdAnchor,
    };
  }
  return {
    eur: eurAnchor,
    usd: Math.round(eurAnchor * r),
  };
}

/**
 * @param {Object} p
 * @param {'graduate_full'|'graduate_ielts'|'families'|'executive'} p.productLine
 * @param {'eur'|'usd'} [p.billingCurrency]
 * @param {'monthly'|'3months'|'12months'|'11months'|'complete'} p.planKey
 * @param {'standard'|'legacy_graduate'} [p.pricingSegment]
 */
export function stripeUrlFor({
  productLine,
  billingCurrency,
  planKey,
  pricingSegment = 'standard',
}) {
  const normalizedBillingCurrency = normalizeBillingCurrency(billingCurrency);
  const normalizedPlanKey = normalizePlanKey(planKey, pricingSegment);

  if (
    productLine === 'graduate_full' &&
    pricingSegment === 'legacy_graduate'
  ) {
    const config = resolveLegacyLinkConfig(normalizedPlanKey);
    if (!config) return null;
    return firstNonEmpty(
      envValue(config.shared),
      envValue(config.eur),
      envValue(config.eurAlias),
      LEGACY_FALLBACKS[normalizedPlanKey]
    );
  }

  const config = resolveStandardLinkConfig(productLine, normalizedPlanKey);
  if (!config) return null;

  if (productLine === 'executive') {
    return firstNonEmpty(envValue(config.shared));
  }

  if (productLine === 'graduate_full' || productLine === 'graduate_ielts') {
    return firstNonEmpty(envValue(config.shared), envValue(config.eur), null);
  }

  return firstNonEmpty(
    envValue(config.shared),
    envValue(config[normalizedBillingCurrency]),
    null
  );
}

/** Families checkout is not on app.liwaza.org — calendar / prospecting (override via env). */
export const FAMILIES_OUTREACH_URL_DEFAULT = 'https://fam.liwaza.org/calendar';

export function familiesOutreachUrl() {
  const v = process.env.REACT_APP_FAMILIES_OUTREACH_URL;
  return typeof v === 'string' && v.trim() ? v.trim() : FAMILIES_OUTREACH_URL_DEFAULT;
}

function resolvePricingCanonicalBase({
  productLine,
  billingCurrency,
  countryCode,
  pricingCanonicalBase,
}) {
  if (productLine === 'graduate_full' || productLine === 'graduate_ielts') {
    return 'eur';
  }
  if (pricingCanonicalBase === 'eur' || pricingCanonicalBase === 'usd') {
    return pricingCanonicalBase;
  }
  const n = normalizeCountryCode(countryCode);
  if (n && (productLine === 'graduate_full' || productLine === 'graduate_ielts')) {
    return graduateCanonicalBaseFromCountryCode(n);
  }
  return normalizeBillingCurrency(billingCurrency) === 'usd' ? 'usd' : 'eur';
}

/**
 * @param {Object} p
 * @param {'graduate_full'|'graduate_ielts'|'families'|'executive'} p.productLine
 * @param {'eur'|'usd'} [p.billingCurrency]
 * @param {string} p.planKey monthly | 3months | 12months | 11months | complete
 * @param {'standard'|'legacy_graduate'} [p.pricingSegment]
 * @param {'EUR'|'USD'|'XOF'|'CAD'} [p.displayCurrency]
 * @param {string|null|undefined} [p.countryCode]
 * @param {'eur'|'usd'} [p.pricingCanonicalBase]
 */
export function subscriptionAmounts({
  productLine,
  billingCurrency,
  planKey,
  pricingSegment = 'standard',
  displayCurrency,
  countryCode,
  pricingCanonicalBase,
}) {
  const normalizedBillingCurrency = normalizeBillingCurrency(billingCurrency);
  const normalizedPlanKey = normalizePlanKey(planKey, pricingSegment);
  const isLegacyGraduate =
    productLine === 'graduate_full' && pricingSegment === 'legacy_graduate';
  const canonicalBase = resolvePricingCanonicalBase({
    productLine,
    billingCurrency,
    countryCode,
    pricingCanonicalBase,
  });
  const normalizedDisplayCurrency = normalizeDisplayCurrency(
    displayCurrency,
    normalizedBillingCurrency === 'usd' ? 'USD' : 'EUR'
  );

  let row;
  if (productLine === 'graduate_full' || productLine === 'graduate_ielts') {
    row = graduateEurUsdRowForCanonicalBase({
      productLine,
      planKey,
      pricingSegment,
      pricingCanonicalBase: canonicalBase,
    });
  } else {
    const lineTable = AMOUNTS[productLine] || AMOUNTS.families;
    row =
      lineTable[normalizedPlanKey] ||
      lineTable.monthly ||
      { eur: 85, usd: Math.round(85 * DISPLAY_CURRENCY_RATES.USD.rate) };
  }

  return {
    amountEur: row.eur,
    amountUsd: row.usd,
    chargeAmount:
      normalizedBillingCurrency === 'usd' ? row.usd : row.eur,
    displayAmount: resolveDisplayAmount({
      productLine,
      planKey: normalizedPlanKey,
      row,
      displayCurrency: normalizedDisplayCurrency,
    }),
    displayCurrency: normalizedDisplayCurrency,
    currencySymbol: DISPLAY_CURRENCY_RATES[normalizedDisplayCurrency].symbol,
    stripeUrl: stripeUrlFor({
      productLine,
      billingCurrency: normalizedBillingCurrency,
      planKey,
      pricingSegment: isLegacyGraduate ? 'legacy_graduate' : 'standard',
    }),
  };
}
